import { useAsyncData, useRuntimeConfig } from '#imports'
import { type MaybeRef, computed, toValue } from 'vue'

import type {
  PaymentCreateFraudData,
  PaymentMethod,
} from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import { useSignifyd } from '@backmarket/nuxt-layer-fraud/useSignifyd'
import { splitHostname } from '@backmarket/utils/url/splitHostname'

// TODO [PAYIN-3532] We should have an API similar to useScriptTag to load Ravelin
// Especially to get retries and logging
const useRavelinLibrary = () => {
  const load = async () => {
    const { default: Ravelin } = await import('ravelinjs/core+track')

    return Ravelin
  }

  return { load }
}

/**
 * Returns a unique device ID for Ravelin. Loads the library if needed.
 */
const useRavelinId = () => {
  const {
    public: { RAVELIN_KEY },
  } = useRuntimeConfig()

  const { load } = useRavelinLibrary()

  // Load the library and initialize the instance
  const getRavelinInstance = async (ravelinKey: string) => {
    const Ravelin = await load()

    const { domain, extension } = splitHostname(window.location.host)

    return new Ravelin({
      key: ravelinKey,

      // As recommended, setting the cookie domain at as high a level as possible,
      // e.g. mysite.com rather than subdomain.mysite.com
      // Read more: https://developer.ravelin.com/libraries-and-sdks/ravelinjs/v1/#reference
      cookieDomain: `${domain}.${extension}`,
    })
  }

  return async () => {
    try {
      const ravelinInstance = await getRavelinInstance(RAVELIN_KEY)

      return ravelinInstance.core.id()
    } catch {
      // Silent catch, the payment should not be blocked by this
    }

    return null
  }
}

/**
 * Returns the fraud data needed to create a payment.
 *
 * To assess the probability of fraud, we rely on third party providers.
 * Depending on the payment method, this composable will
 * - load the needed third-party library
 * - get and format the fraud data needed for the payment creation
 */
export const useFraudData = (
  paymentMethod: MaybeRef<PaymentMethod | undefined | null>,
) => {
  const { load: getSignifydSessionId } = useSignifyd({ trigger: 'manual' })
  const getRavelinDeviceId = useRavelinId()

  const fraudCheckPartner = computed(() => {
    const method = toValue(paymentMethod)

    if (!method || method.fraudCheckPartner === '') {
      return null
    }

    /**
     * https://backmarket.atlassian.net/browse/PAYIN-5529
     * Based on A/B test case, we may want to override partner configuration.
     */
    if (method.config?.fraudCheckPartnerOverride) {
      return method.config.fraudCheckPartnerOverride
    }

    return method.fraudCheckPartner
  })

  const fraudProviderAsyncKey = computed(() => {
    const partner = fraudCheckPartner.value

    return partner ? `fraud-provider-${partner}` : 'no-fraud-provider-async-key'
  })

  const asyncDataResult = useAsyncData(
    fraudProviderAsyncKey,
    async (): Promise<PaymentCreateFraudData> => {
      let signifydSessionId = null
      let ravelinDeviceId = null

      switch (fraudCheckPartner.value) {
        case 'signifyd':
          signifydSessionId = await getSignifydSessionId()
          break
        case 'ravelin':
          ravelinDeviceId = await getRavelinDeviceId()
          break
        case 'sygnifyd_ravelin': {
          /**
           * https://backmarket.atlassian.net/browse/PAYIN-5529
           * We temporarily send both fraud partners’ data to be able to compare fraud providers.
           */
          const [signifydId, ravelinId] = await Promise.all([
            getSignifydSessionId(),
            getRavelinDeviceId(),
          ])
          signifydSessionId = signifydId
          ravelinDeviceId = ravelinId
          break
        }
        default:
          break
      }

      return {
        ...(signifydSessionId && { signifyd_fingerprint: signifydSessionId }),
        ...(ravelinDeviceId && { deviceId: ravelinDeviceId }),
      }
    },
    {
      server: false,
    },
  )

  return asyncDataResult
}
